
import { FileDownloadOutlined, InfoOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardHeader, CircularProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { actionTwoButtonStyle, cardStyle } from '../../theme';


const CampaignCallDetailsDialog = ({ open, data, campaignId, inputStartDate, inputEndDate, segments, campaignName, agentDispoData }) => {
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const systemAgents = ['0000000', 'System', 'SS Service', 'RingCX']

	const [agentRetryDispos, setAgentRetryDispos] = useState([]);
	const [agentFinalDispos, setAgentFinalDispos] = useState([]);
	const [systemRetryDispos, setSystemRetryDispos] = useState([]);
	const [systemFinalDispos, setSystemFinalDispos] = useState([]);


	useEffect(() => {
		if (open && data) {
			console.log(segments)
			setLoading(true);

			const allAgentRetry = [];
			const allAgentFinal = [];
			const allSystemRetry = [];
			const allSystemFinal = [];

			for (const status of data) {
				for (const disposition of status.disposition.buckets) {
					if (disposition.agent.buckets) {
						let agentCount = 0;
						let systemCount = 0;
						for (const agent of disposition.agent.buckets) {
							if (systemAgents.includes(agent.key) || !agent.key) {
								systemCount = systemCount + agent.doc_count;
							} else {
								agentCount = agentCount + agent.doc_count;
							}
						}
						if (status.key === 'completed') {
							if (systemCount > 0) {
								allSystemFinal.push({ label: disposition.key, value: systemCount });
							}
							if (agentCount > 0) {
								allAgentFinal.push({ label: disposition.key, value: agentCount, agents: disposition.agent.buckets.sort((a, b) => b.doc_count - a.doc_count) });
							}
						} else {
							if (systemCount > 0) {
								allSystemRetry.push({ label: disposition.key, value: systemCount });
							}
							if (agentCount > 0) {
								allAgentRetry.push({ label: disposition.key, value: agentCount, agents: disposition.agent.buckets.sort((a, b) => b.doc_count - a.doc_count) });
							}
						}
					}
				}
			}

			allAgentRetry.sort((a, b) => b.value - a.value);
			allAgentFinal.sort((a, b) => b.value - a.value);
			allSystemRetry.sort((a, b) => b.value - a.value);
			allSystemFinal.sort((a, b) => b.value - a.value);

			setAgentRetryDispos(allAgentRetry);
			setAgentFinalDispos(allAgentFinal);
			setSystemRetryDispos(allSystemRetry);
			setSystemFinalDispos(allSystemFinal);

			setLoading(false);
		}
	}, [open, data]);

	// Build a lookup from disposition label to its agent breakdown for grouped dispositions
	function getAgentsForDisposition(dispoLabel, statusKey) {
		if (!data) return [];
		for (const status of data) {
			if ((statusKey === 'completed' && status.key !== 'completed') || (statusKey === 'retry' && status.key === 'completed')) continue;
			for (const disposition of status.disposition.buckets) {
				if (disposition.key === dispoLabel && disposition.agent?.buckets) {
					return disposition.agent.buckets
						.filter(a => !systemAgents.includes(a.key) && a.key)
						.sort((a, b) => b.doc_count - a.doc_count);
				}
			}
		}
		return [];
	}

	function GetRow(props) {
		const [open, setOpen] = useState(false);
		const { disposition, agent, status, groupLabel } = props;
		const isSystem = agent === 'system';
		const hasGroupColumn = groupLabel !== undefined;

		return (
			<>
				<TableRow key={disposition.label} sx={{ cursor: isSystem ? 'default' : 'pointer' }}>
					{hasGroupColumn && groupLabel && (
						<TableCell
							sx={{
								fontWeight: 'bold',
								verticalAlign: 'middle',
								borderRight: '3px solid',
								borderRightColor: 'primary.main',
								textAlign: 'center',
								whiteSpace: 'nowrap',
							}}
						>
							{groupLabel}
						</TableCell>
					)}
					{hasGroupColumn && !groupLabel && (
						<TableCell sx={{ borderRight: '3px solid', borderRightColor: 'primary.main' }} />
					)}
					{!isSystem && disposition?.agents && disposition.agents.length > 0 && <TableCell onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}</TableCell>}
					{!isSystem && (!disposition?.agents || disposition.agents.length === 0) && <TableCell />}
					{isSystem && <TableCell />}
					<TableCell onClick={!isSystem ? () => setOpen(!open) : undefined}>
						{disposition.label}
						{disposition.label === 'Agent Specific — Agent Unavailable' && <Tooltip title="This disposition appears when before placing the dial, there are no agents available to take the call. Instead of dialing the contact, the system will send in a suppressed disposition. These dials won't appear on your contact center reporting">
							<InfoOutlined color="primary" />
						</Tooltip>}
					</TableCell>
					<TableCell onClick={!isSystem ? () => setOpen(!open) : undefined}>{disposition.value}</TableCell>
					<TableCell align="right">
						<Tooltip title='Download CSV'>
							<IconButton onClick={() => {
								downloadCSV(status, agent, disposition.label);
							}}>
								<FileDownloadOutlined color="primary" />
							</IconButton>
						</Tooltip>
					</TableCell>
				</TableRow>
				{!isSystem && <TableRow key={disposition.label + 'details'} sx={{ display: open ? 'table-row' : 'none' }}>
					<TableCell colSpan={hasGroupColumn ? 5 : 4}>
						<Table sx={{ width: '100%' }}>
							<TableHead>
								<TableRow>
									<TableCell>Agent</TableCell>
									<TableCell>Count</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{disposition?.agents?.map((agent) => (
									<TableRow key={agent.key}>
										<TableCell>{agent.key}</TableCell>
										<TableCell>{agent.doc_count}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableCell>
				</TableRow>}
			</>
		);
	}

	const downloadCSV = async (status, agentType, dispositionFilter) => {
		try {
			const session = await fetchAuthSession();
			await post({
				apiName: 'cdyxoutreach',
				path: '/export/call-details',
				options: {
					body: {
						campaignId,
						campaignName,
						startDate: inputStartDate ? inputStartDate : moment().startOf('day').toISOString(),
						endDate: inputEndDate ? inputEndDate : moment().toISOString(),
						status: status || null,
						agentType: agentType || null,
						dispositionFilter: dispositionFilter || null,
						segments,
						hideExternalId: userContext.telephonyProvider === 'Webex'
					},
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey
					}
				}
			}).response;

			enqueueSnackbar('Report generation started. You will receive a notification when it is ready.', { variant: 'success' });
		} catch (err) {
			console.error('Failed to request call details report:', err);
			enqueueSnackbar('Failed to start report generation.', { variant: 'error' });
		}
	}

	return (
		<Box display='flex' flexDirection="column" justifyContent='center' sx={{ width: '100%' }} gap={2}>
			{!loading && <Box sx={{ width: '100%' }} display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="Agent Disposition Final" action={
						<Box display='flex' gap={1} alignItems='center' justifyContent='center'>
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('completed', 'agent')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Retry dispositions seen here mean the contact met the max number attempts for the campaign">
								<InfoOutlined color="primary" />
							</Tooltip>
						</Box>
					} />
					<Table sx={{ width: '100%' }}>
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: '180px', whiteSpace: 'nowrap' }}>Group</TableCell>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{agentDispoData?.final?.length > 0 ? (
								<>
									{agentDispoData.final.map((group) => (
										group.by_name.buckets.length > 0 ? (
											group.by_name.buckets.map((dispo, idx) => {
												const agents = getAgentsForDisposition(group.key, 'completed');
												const dispoObj = { label: dispo.key, value: dispo.doc_count, agents };
												return <GetRow key={`final-${group.key}-${dispo.key}`} disposition={dispoObj} status='completed' agent='agent' groupLabel={idx === 0 ? group.key : ''} />;
											})
										) : (
											(() => {
												const agents = getAgentsForDisposition(group.key, 'completed');
												return <GetRow key={`final-${group.key}`} disposition={{ label: group.key, value: group.doc_count, agents }} status='completed' agent='agent' groupLabel={group.key} />;
											})()
										)
									))}
									{agentFinalDispos
										.filter(d => !agentDispoData.final.some(g => g.key === d.label))
										.map((disposition) => <GetRow key={`final-ungrouped-${disposition.label}`} disposition={disposition} status='completed' agent='agent' groupLabel={disposition.label} />)
									}
								</>
							) : (
								agentFinalDispos.map((disposition) => <GetRow key={`final-${disposition.label}`} disposition={disposition} status='completed' agent='agent' groupLabel={disposition.label} />)
							)}
						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="Agent Disposition Retry"
						action={
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('retry', 'agent')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>}
					/>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: '180px', whiteSpace: 'nowrap' }}>Group</TableCell>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{agentDispoData?.retry?.length > 0 ? (
								<>
									{agentDispoData.retry.map((group) => (
										group.by_name.buckets.length > 0 ? (
											group.by_name.buckets.map((dispo, idx) => {
												const agents = getAgentsForDisposition(group.key, 'retry');
												const dispoObj = { label: dispo.key, value: dispo.doc_count, agents };
												return <GetRow key={`retry-${group.key}-${dispo.key}`} disposition={dispoObj} status='retry' agent='agent' groupLabel={idx === 0 ? group.key : ''} />;
											})
										) : (
											(() => {
												const agents = getAgentsForDisposition(group.key, 'retry');
												return <GetRow key={`retry-${group.key}`} disposition={{ label: group.key, value: group.doc_count, agents }} status='retry' agent='agent' groupLabel={group.key} />;
											})()
										)
									))}
									{agentRetryDispos
										.filter(d => !agentDispoData.retry.some(g => g.key === d.label))
										.map((disposition) => <GetRow key={`retry-ungrouped-${disposition.label}`} disposition={disposition} status='retry' agent='agent' groupLabel={disposition.label} />)
									}
								</>
							) : (
								agentRetryDispos.map((disposition) => <GetRow key={`retry-${disposition.label}`} disposition={disposition} status='retry' agent='agent' groupLabel={disposition.label} />)
							)}
						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="System Disposition Final" action={
						<Box display='flex' gap={1} alignItems='center' justifyContent='center'>
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('completed', 'system')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Retry dispositions seen here mean the contact met the max number attempts for the campaign">
								<InfoOutlined color="primary" />
							</Tooltip>
						</Box>} />
					<Table>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{systemFinalDispos.map((disposition) => <GetRow disposition={disposition} status='completed' agent='system' />)}

						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="System Disposition Retry"
						action={
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('retry', 'system')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>}
					/>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{systemRetryDispos.map((disposition) => <GetRow disposition={disposition} status='retry' agent='system' />)}
						</TableBody>
					</Table>
				</Card>

			</Box>}
			{!loading && <Button sx={actionTwoButtonStyle} onClick={() => downloadCSV()} >Download All Data</Button>}
			{loading ? <CircularProgress /> : null}
		</Box>

	);
};

export default CampaignCallDetailsDialog;
